/**
 * InviteLanding Page
 *
 * File: Mythbound.App/src/pages/InviteLanding.tsx
 * Created: 2026-01-06
 * Rewritten: 2026-09-22 (PRD001A 001A.6 — invite frontend)
 *
 * Purpose: Handle campaign invite preview and joining.
 * WHO: Players clicking on invite links (anonymous or signed in).
 * WHAT: Preview a token anonymously, then gate join on login/onboarding/
 * membership, then join via the ONE real backend join path.
 * WHERE: Route: /invite/:token
 * WHY: Provide a smooth, honest onboarding for invited players.
 *
 * Contract: shared/contracts/campaign/v1/campaign-management.yaml
 *   § WebInvitationAdmission_001A6
 *
 * INTEGRATION POINTS:
 * - GET /api/invite/{token} — anonymous preview (campaignService.getInvitePreview)
 * - POST /api/campaigns/{id}/participants — the ONLY join operation
 *   (campaignService.joinCampaignWithToken); the previous version of this
 *   file called a nonexistent POST /api/campaigns/{id}/join — REMOVED.
 * - GET /api/campaigns/{id} — used ONLY as an already-member pre-check.
 *   PRD001A R7 (2026-09-22): only a CONFIRMED 403 ("not yet a member") may
 *   offer joining. 401 (session expired) routes back through login;
 *   anything else (network/unavailable/unexpected) is inconclusive and
 *   shows a distinct, retryable 'membership-check-failed' screen — it never
 *   falls through to "ready". The join call itself remains the
 *   authoritative check regardless.
 *
 * STATE MACHINE (item 4): rather than one big imperatively-transitioned
 * enum, the rendered `PageState` is DERIVED each render from three small,
 * independently-owned pieces — `previewStatus` (the one-time anonymous
 * fetch), `gateStatus` (login/onboarding/membership), and `joinStatus` (the
 * join action itself). This avoids effects fighting over a shared enum and
 * makes "a join-time 409 re-uses the SAME 'expired'/'revoked'/'invalid'
 * states the preview would have shown" (item 4's "honor revocation/expiry
 * discovered after the preview") a one-line override rather than a new
 * parallel error bucket.
 *
 * SECURITY (item 3): a pending invite is preserved across login and policy
 * review via `AuthContext`'s `setPendingInviteToken`/`useInviteReturnPath` — a
 * same-origin-only client hint that decides WHERE the browser navigates
 * next, never whether a join succeeds. Every join attempt re-validates the
 * token and membership against the database (see `CampaignService`). The
 * token is never passed to `console.*` anywhere in this file.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router";
import {
  Box,
  Button,
  Heading,
  Text,
  Spinner,
  VStack,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Container,
  Divider,
  Badge,
  Center,
  Fade,
  useDisclosure,
} from "@chakra-ui/react";
import { CheckCircle, AlertCircle, Home, Users, LogIn, UserCheck, Ban } from "lucide-react";
import {
  campaignService,
  getInviteErrorCode,
  getInviteErrorMessage,
  type InvitePreviewResponse,
} from "../services/api/CampaignService";
import { ApiError } from "../services/api/ApiError";
import { useAuth, setPendingInviteToken, deriveOnboardingEligibility } from "../context/AuthContext";
import ScreenNameModal from "../components/ScreenNameModal";
import { ServiceUnavailable } from "../components/common/ServiceUnavailable";

/** The one-time anonymous preview fetch, keyed only by the token. */
type PreviewStatus = "loading" | "invalid" | "expired" | "revoked" | "unavailable" | "loaded";
/**
 * Login/onboarding/membership gating — only meaningful once `previewStatus === 'loaded'`.
 *
 * `'membership-check-failed'` (PRD001A R7, 2026-09-22): the pre-check
 * (`GET /api/campaigns/{id}`) used to treat EVERY rejection — a confirmed
 * 403 "not a member yet", an expired-login 401, a network failure, an
 * unrelated 5xx, or any other unexpected status — identically, as "ready to
 * join". Only a confirmed 403 means confirmed nonmembership; everything
 * else is INCONCLUSIVE and must never be read as ready-to-join. This state
 * covers the inconclusive cases with an explicit, retryable "couldn't
 * verify" screen instead.
 *
 * `'onboarding-check-failed'` (PRD001A R7a correction, 2026-09-23): profile
 * enrichment (`/auth/me`, via `AuthContext.enrichProfile`) can SETTLE
 * (finish trying) without ever producing a verified `needsScreenName`
 * answer — a non-OK response or a thrown error. The R7 fix treated that
 * settled-but-still-`undefined` result as "does not need a screen name"
 * (falling through to the membership check), which is the R7a defect: a
 * finished request is not a resolved eligibility answer. This state covers
 * that case with its own explicit, retryable "couldn't verify" screen — see
 * `deriveOnboardingEligibility` in `AuthContext.tsx`.
 */
type GateStatus =
  | "checking"
  | "login-required"
  | "onboarding-required"
  | "legal-acceptance-required"
  | "legal-status-forbidden"
  | "onboarding-check-failed"
  | "membership-check-failed"
  | "already-member"
  | "ready";
/**
 * The join action itself.
 *
 * `'forbidden'` (PRD001A R7): a REAL 403 from the join endpoint itself
 * (distinct from the membership pre-check's 403, which means "not a member
 * yet" and is expected) — e.g. banned/removed-with-restriction, or any other
 * authorization rule the join endpoint enforces beyond plain membership.
 */
type JoinStatus = "idle" | "joining" | "joined" | "full" | "forbidden";

/** The rendered state — see module header for how this is derived, not stored. */
type PageState =
  | "loading"
  | "invalid"
  | "expired"
  | "revoked"
  | "unavailable"
  | "login-required"
  | "onboarding-required"
  | "legal-acceptance-required"
  | "legal-status-forbidden"
  | "onboarding-check-failed"
  | "membership-check-failed"
  | "already-member"
  | "ready"
  | "joining"
  | "joined"
  | "full"
  | "forbidden";

/**
 * Keep preview, gate, and join outcomes separate, then apply precedence so an
 * invalid preview cannot expose campaign details or later join states.
 */
function derivePageState(previewStatus: PreviewStatus, gateStatus: GateStatus, joinStatus: JoinStatus): PageState {
  if (previewStatus !== "loaded") return previewStatus;
  if (joinStatus === "joining") return "joining";
  if (joinStatus === "joined") return "joined";
  if (joinStatus === "full") return "full";
  if (joinStatus === "forbidden") return "forbidden";
  if (gateStatus === "checking") return "loading";
  return gateStatus;
}

export const InviteLanding: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { authStatus, userInfo, profileEnrichmentSettled, legalAcceptance, legalAcceptanceStatus, checkSessionStatus } = useAuth();

  const [previewStatus, setPreviewStatus] = useState<PreviewStatus>("loading");
  const [campaignInfo, setCampaignInfo] = useState<InvitePreviewResponse | null>(null);
  const [gateStatus, setGateStatus] = useState<GateStatus>("checking");
  const [joinStatus, setJoinStatus] = useState<JoinStatus>("idle");
  const [detailMessage, setDetailMessage] = useState<string | null>(null);
  // Manual retry for the membership pre-check (PRD001A R7) — bumped by the
  // "Try again" button on the 'membership-check-failed' screen so a
  // transient network/unavailable failure isn't a dead end.
  const [membershipCheckRetryToken, setMembershipCheckRetryToken] = useState(0);

  // Synchronous duplicate-submit guard — belt-and-suspenders alongside the
  // `joinStatus === 'joining'` check, which disables the button but is only
  // applied on the NEXT render, not before a rapid second click's handler
  // has already started running.
  const joinInFlightRef = useRef(false);
  // The post-join "Welcome!" auto-navigate — cleared on unmount so a
  // background navigate() never fires after the page is gone.
  const navigateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const joinScopeRef = useRef(0);

  // A request may commit after navigation/account change; that result belongs
  // to its original viewer and invite. Ignore its UI/timer work while leaving
  // server-side idempotency in charge of any retry.
  useEffect(() => {
    joinInFlightRef.current = false;
    setJoinStatus("idle");
    return () => {
      joinScopeRef.current += 1;
      if (navigateTimerRef.current) clearTimeout(navigateTimerRef.current);
    };
  }, [token, userInfo?.id, authStatus]);

  const {
    isOpen: isScreenNameModalOpen,
    onOpen: onScreenNameModalOpen,
    onClose: onScreenNameModalClose,
  } = useDisclosure();

  // ---------------------------------------------------------------------
  // Step 1: anonymous preview — GET /api/invite/{token}. Works with no
  // login (item 1). Never shows campaign details for an invalid/expired/
  // revoked token (item 4) — those branches below never touch campaignInfo.
  // ---------------------------------------------------------------------
  // Preview depends only on the invite token and remains anonymous; cleanup
  // prevents an old route result from revealing details after token changes.
  useEffect(() => {
    if (!token) {
      setPreviewStatus("invalid");
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    setPreviewStatus("loading");
    setCampaignInfo(null);
    setGateStatus("checking");
    setJoinStatus("idle");
    setDetailMessage(null);

    campaignService
      .getInvitePreview(token, { signal: controller.signal })
      .then((info) => {
        if (cancelled) return;
        setCampaignInfo(info);
        setPreviewStatus("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof DOMException && err.name === "AbortError") return;
        const code = getInviteErrorCode(err);
        if (code === "invite_expired") {
          setPreviewStatus("expired");
          return;
        }
        if (code === "invite_revoked") {
          setPreviewStatus("revoked");
          return;
        }
        if (err instanceof ApiError && (err.kind === "unavailable" || err.kind === "network")) {
          setPreviewStatus("unavailable");
          setDetailMessage(err.message);
          return;
        }
        // Only the server's explicit token verdict means invalid. A malformed
        // response, rate limit or unknown failure cannot establish validity.
        setPreviewStatus(code === "invite_invalid" ? "invalid" : "unavailable");
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [token]);

  // ---------------------------------------------------------------------
  // Step 2: gate on login -> legal acceptance -> onboarding -> membership,
  // in that order. Screen-name setup stays inline; login and policy review
  // can leave this route and preserve the invite through the shared return path.
  // ---------------------------------------------------------------------
  useEffect(() => {
    // Ordered access gate: confirmed login, current legal status, completed
    // onboarding, then the membership pre-check.
    if (previewStatus !== "loaded" || !campaignInfo) return;

    if (authStatus === "loading") {
      setGateStatus("checking");
      return;
    }
    if (authStatus === "unavailable") {
      setPreviewStatus("unavailable");
      setDetailMessage("We couldn't verify your sign-in right now.");
      return;
    }
    if (authStatus !== "authenticated") {
      setDetailMessage(null); // plain "not logged in yet" — no session-expired message to show
      setGateStatus("login-required");
      return;
    }
    // A confirmed session is not eligible until legal status is verified by
    // the server. Missing, invalid, and unavailable responses stay fail-closed.
    if (legalAcceptanceStatus === "pending") {
      setGateStatus("checking");
      return;
    }
    if (legalAcceptanceStatus === "unauthenticated") {
      setDetailMessage('Your session expired while checking policy status. Please log in again.');
      setGateStatus('login-required');
      return;
    }
    if (legalAcceptanceStatus === "forbidden") {
      setDetailMessage('The server denied access to your policy acceptance status.');
      setGateStatus('legal-status-forbidden');
      return;
    }
    if (legalAcceptanceStatus === "unavailable" || legalAcceptanceStatus === "update-required") {
      setDetailMessage(legalAcceptanceStatus === "update-required"
        ? "The server requires policy text that is not available in this version of the app."
        : "We couldn't verify your server-recorded policy acceptance. Please retry in a moment.");
      setGateStatus("onboarding-check-failed");
      return;
    }
    if (legalAcceptanceStatus === "required") {
      setGateStatus("legal-acceptance-required");
      return;
    }
    if (legalAcceptanceStatus !== "accepted" || !legalAcceptance) {
      setGateStatus("onboarding-check-failed");
      setDetailMessage("We couldn't verify your server-recorded policy acceptance. Please retry in a moment.");
      return;
    }

    // ONBOARDING-ORDER FIX (PRD001A R7) + R7a CORRECTION (2026-09-23):
    // `AuthContext.checkSessionStatus` sets `authStatus = 'authenticated'`
    // as soon as the session check succeeds, but `needsScreenName` is only
    // populated afterward by a separate, best-effort profile-enrichment call
    // (see `AuthContext.tsx`'s `enrichProfile`/`deriveOnboardingEligibility`
    // header comments) — so `userInfo?.needsScreenName` can be `undefined`
    // for a real window right after login, not just `true`/`false`.
    // `deriveOnboardingEligibility` turns the two raw signals
    // (`needsScreenName`, `profileEnrichmentSettled`) into one explicit
    // answer instead of re-deriving it here (the R7a defect: a settled-but-
    // still-`undefined` result — enrichment finished trying but never got a
    // verified answer — fell through the old checks the same way a
    // confirmed "does not need a screen name" would, silently letting Join
    // become reachable on an inconclusive lookup):
    //   - 'unknown'                    → still resolving; stay in the
    //     loading/checking state (not "required", which would wrongly pop
    //     the modal; not "ready", which is the R7a bug).
    //   - 'unresolved'                 → enrichment settled without ever
    //     producing a verified answer (`/auth/me` degraded or threw). Show
    //     the distinct, retryable 'onboarding-check-failed' screen — never
    //     silently proceed, and never guess "required" either.
    //   - 'resolved-needs-screen-name' → confirmed: show the onboarding modal.
    //   - 'resolved-eligible'          → confirmed: proceed to the
    //     membership check below.
    if (legalAcceptance.needsScreenName) {
      setGateStatus("onboarding-required");
      return;
    }
    const onboardingEligibility = deriveOnboardingEligibility(userInfo?.needsScreenName, profileEnrichmentSettled);
    if (onboardingEligibility === "unknown") {
      setGateStatus("checking");
      return;
    }
    if (onboardingEligibility === "unresolved") {
      // Deliberately avoids the words "unavailable"/"couldn't verify"/"try
      // again" here — the rendered screen's own retry button already says
      // "Try again" (from the shared `ServiceUnavailable` component), and
      // duplicating that phrasing in the body text would make a plain
      // text-content assertion match more than one element.
      setDetailMessage("We're unable to confirm whether you still need to set up a screen name. Please retry in a moment.");
      setGateStatus("onboarding-check-failed");
      return;
    }
    if (onboardingEligibility === "resolved-needs-screen-name") {
      setGateStatus("onboarding-required");
      return;
    }
    if (!legalAcceptance.eligible) {
      setDetailMessage("We couldn't confirm that your account is eligible yet. Please retry in a moment.");
      setGateStatus("onboarding-check-failed");
      return;
    }

    // Authenticated and onboarded (verified 'resolved-eligible') — check membership once (item: distinct
    // 'already-member' state). The join endpoint itself has no response
    // field distinguishing a new join from an idempotent re-join, so this
    // is the only way to show that distinction BEFORE the user clicks Join.
    //
    // Only a CONFIRMED 403 ("not a member yet") may offer joining (PRD001A
    // R7). Every other outcome is inconclusive and must not be read as
    // ready-to-join:
    // - 401: the session expired between the top-level authStatus check
    //   above and this call — route back through the same login-required
    //   path (the pending-invite token is preserved by `handleLoginClick`).
    // - network/unavailable/not_found/conflict/unknown: the pre-check
    //   itself failed to produce an answer — show a distinct, retryable
    //   'membership-check-failed' screen. The join call is still the
    //   authoritative check server-side, but the UI must not pre-emptively
    //   claim "ready" on a check that never actually confirmed nonmembership.
    let cancelled = false;
    setGateStatus("checking");
    campaignService
      .getCampaign(campaignInfo.campaign_id)
      .then(() => {
        if (!cancelled) setGateStatus("already-member");
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.kind === "forbidden") {
          setGateStatus("ready");
          return;
        }
        if (err instanceof ApiError && err.kind === "unauthenticated") {
          setDetailMessage("Your session expired. Please log in again to join.");
          setGateStatus("login-required");
          return;
        }
        setDetailMessage(err instanceof ApiError ? err.message : null);
        setGateStatus("membership-check-failed");
      });

    return () => {
      cancelled = true;
    };
  }, [previewStatus, campaignInfo, authStatus, userInfo?.id, userInfo?.needsScreenName, profileEnrichmentSettled, legalAcceptanceStatus, legalAcceptance, membershipCheckRetryToken]);

  // Re-open the screen-name modal whenever gating lands there — mirrors
  // Dashboard.tsx's own gate so the two behave identically.
  useEffect(() => {
    if (gateStatus === "onboarding-required") {
      onScreenNameModalOpen();
    }
  }, [gateStatus, onScreenNameModalOpen]);

  const pageState = derivePageState(previewStatus, gateStatus, joinStatus);

  // sessionStorage preserves this invite across login in this tab; it is a
  // navigation hint only and grants no authority to join.
  const handleLoginClick = useCallback(() => {
    if (token) setPendingInviteToken(token);
    navigate("/login");
  }, [token, navigate]);

  // Route through the existing Layout policy gate while retaining this invite
  // as a navigation hint; only verified server acceptance and onboarding
  // eligibility let useInviteReturnPath resume it.
  const handlePolicyReviewClick = useCallback(() => {
    if (!token) return;
    setPendingInviteToken(token);
    navigate("/dashboard");
  }, [token, navigate]);

  // The join endpoint is the authoritative mutation and can reject conditions
  // that preview and the membership pre-check cannot establish.
  const handleJoinCampaign = useCallback(async () => {
    if (!campaignInfo || !token) return;
    if (joinInFlightRef.current) return; // duplicate-submit prevention
    const joinScope = joinScopeRef.current;
    joinInFlightRef.current = true;
    setJoinStatus("joining");
    setDetailMessage(null);

    try {
      await campaignService.joinCampaignWithToken(campaignInfo.campaign_id, token);
      if (joinScope !== joinScopeRef.current) return;
      setJoinStatus("joined");
      navigateTimerRef.current = setTimeout(() => {
        if (joinScope === joinScopeRef.current) navigate(`/campaigns/${campaignInfo.campaign_id}`);
      }, 1500);
    } catch (err) {
      if (joinScope !== joinScopeRef.current) return;
      const code = getInviteErrorCode(err);
      if (code === "campaign_full") {
        setJoinStatus("full");
        setDetailMessage(getInviteErrorMessage(err, "This campaign is full."));
      } else if (code === "invite_expired") {
        // Honor expiry discovered after the preview (item 4) — reuse the
        // same 'expired' state the preview path uses.
        setJoinStatus("idle");
        setPreviewStatus("expired");
      } else if (code === "invite_revoked") {
        setJoinStatus("idle");
        setPreviewStatus("revoked");
      } else if (code === "invite_invalid") {
        setJoinStatus("idle");
        setPreviewStatus("invalid");
      } else if (code === "forbidden") {
        // A REAL forbidden from the join endpoint itself (PRD001A R7) —
        // distinct from the membership pre-check's 403, which just means
        // "not a member yet" and is what put the user on this ready/join
        // screen in the first place. This is some other authorization rule
        // the join endpoint enforces (e.g. banned/removed-with-restriction).
        setJoinStatus("forbidden");
        setDetailMessage(getInviteErrorMessage(err, "You don't have permission to join this campaign."));
      } else if (err instanceof ApiError && err.kind === "unauthenticated") {
        // Login expired between rendering the Join button and clicking it
        // (PRD001A R7) — route back through the SAME login-required screen
        // (reusing its existing "Log in to join" button/handler, which
        // already preserves the pending invite token), rather than
        // collapsing this into a generic "unavailable" failure.
        setJoinStatus("idle");
        setGateStatus("login-required");
        setDetailMessage("Your session expired. Please log in again to join.");
      } else if (err instanceof ApiError && (err.kind === "unavailable" || err.kind === "network")) {
        // A real infrastructure failure — never signs the user out or
        // implies anything about the invite/membership itself.
        setJoinStatus("idle");
        setPreviewStatus("unavailable");
        setDetailMessage(getInviteErrorMessage(err, "We couldn't reach the service right now. Please try again."));
      } else {
        // Any other unexpected failure (unknown status, malformed body,
        // etc.) — still an honest "something went wrong", not a silent
        // success or a guessed-at specific state.
        setJoinStatus("idle");
        setPreviewStatus("unavailable");
        setDetailMessage(getInviteErrorMessage(err, "Failed to join campaign."));
      }
    } finally {
      if (joinScope === joinScopeRef.current) joinInFlightRef.current = false;
    }
  }, [campaignInfo, token, navigate]);

  // =======================================================================
  // RENDER
  // =======================================================================

  if (pageState === "loading") {
    return (
      <Center h="100vh">
        <VStack spacing={4}>
          <Spinner size="lg" color="blue.500" thickness="4px" />
          <Text>Loading invite...</Text>
        </VStack>
      </Center>
    );
  }

  if (pageState === "joined") {
    return (
      <Center h="100vh">
        <Fade in={true}>
          <VStack spacing={4}>
            <CheckCircle size={64} color="green" />
            <Heading size="lg">Welcome!</Heading>
            <Text>Redirecting to campaign...</Text>
            <Spinner size="sm" />
          </VStack>
        </Fade>
      </Center>
    );
  }

  if (pageState === "already-member") {
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="green.200">
          <CardHeader>
            <HStack>
              <UserCheck size={24} color="green" />
              <Heading size="md">You're already in this campaign</Heading>
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>
                You're already a member of{" "}
                <Text as="span" fontWeight="bold">
                  {campaignInfo?.campaign_name}
                </Text>
                .
              </Text>
              <Button
                onClick={() => campaignInfo && navigate(`/campaigns/${campaignInfo.campaign_id}`)}
                colorScheme="green"
                w="full"
              >
                Go to Campaign
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (pageState === "login-required") {
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="blue.200">
          <CardHeader>
            <HStack>
              <LogIn size={24} />
              <Heading size="md">Log in to join</Heading>
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>
                {/* PRD001A R7: reused verbatim for BOTH the initial "never
                    logged in" case and an expired login discovered mid-join
                    — `detailMessage` is only set in the latter case. */}
                {detailMessage ?? (
                  <>
                    You'll need to log in before joining{" "}
                    <Text as="span" fontWeight="bold">
                      {campaignInfo?.campaign_name}
                    </Text>
                    . We'll bring you right back here afterward.
                  </>
                )}
              </Text>
              <Button onClick={handleLoginClick} colorScheme="blue" w="full" leftIcon={<LogIn size={18} />}>
                Log in to join
              </Button>
              <Button onClick={() => navigate("/")} variant="ghost" w="full">
                Cancel
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (pageState === "onboarding-required") {
    return (
      <>
        <Center h="100vh">
          <VStack spacing={4}>
            <Spinner size="lg" color="blue.500" thickness="4px" />
            <Text>Finish setting up your profile to join {campaignInfo?.campaign_name}...</Text>
          </VStack>
        </Center>
        <ScreenNameModal isOpen={isScreenNameModalOpen} onClose={onScreenNameModalClose} />
      </>
    );
  }

  if (pageState === "full") {
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="red.200">
          <CardHeader>
            <HStack>
              <Ban size={24} color="red" />
              <Heading size="md">Campaign is full</Heading>
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>{detailMessage ?? "This campaign is at capacity."}</Text>
              <Button onClick={() => navigate("/")} leftIcon={<Home size={16} />} w="full">
                Back to Home
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  // PRD001A R7: the membership pre-check couldn't confirm nonmembership
  // (401 already routes to login-required above; this is network/
  // unavailable/unexpected). Distinct, retryable — never falls through to
  // "ready" on an inconclusive check, and never implies invalid/expired/
  // revoked/forbidden (which are all specific, confirmed outcomes).
  if (pageState === "membership-check-failed") {
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="orange.200">
          <CardHeader>
            <HStack>
              <AlertCircle size={24} color="orange" />
              <Heading size="md">Couldn't verify membership</Heading>
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>
                {detailMessage ?? "We couldn't check your membership in this campaign right now."}
              </Text>
              <Button onClick={() => setMembershipCheckRetryToken((n) => n + 1)} colorScheme="blue" w="full">
                Try again
              </Button>
              <Button onClick={() => navigate("/")} variant="ghost" w="full">
                Back to Home
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  // PRD001A R7a correction (2026-09-23): profile enrichment (`/auth/me`)
  // FINISHING is not the same as onboarding eligibility being RESOLVED — a
  // non-OK response or a thrown error settles `profileEnrichmentSettled`
  // without ever producing a verified `needsScreenName` answer. Reading that
  // as "does not need a screen name" (the R7a defect) let an ordinary
  // membership pre-check 403 enable Join before onboarding was actually
  // known. This distinct, retryable screen is shown instead — reuses the
  // same `ServiceUnavailable` component the rest of the app already uses for
  // an auth-store outage, rather than inventing a second one. Join is never
  // reachable from here, and the pending invite token is left untouched
  // (still in sessionStorage) so it can resume once a fresh check produces a
  // definitive answer. Retrying re-runs the FULL session+enrichment check
  // (`AuthContext.checkSessionStatus`) rather than adding a second, parallel
  // retry mechanism next to the membership pre-check's own.
  if (pageState === "legal-acceptance-required") {
    return (
      <Container maxW="container.md" py={12}>
        <Card>
          <CardHeader><Heading size="md">Review required policies</Heading></CardHeader>
          <CardBody>
            <Text>Please review and accept the Terms of Service and Privacy Policy before joining this campaign.</Text>
            <Button onClick={handlePolicyReviewClick} colorScheme="blue" w="full">
              Review policies
            </Button>
          </CardBody>
        </Card>
      </Container>
    );
  }

  if (pageState === "legal-status-forbidden") {
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="red.200">
          <CardHeader><Heading size="md">Policy status access denied</Heading></CardHeader>
          <CardBody><Text>{detailMessage}</Text></CardBody>
        </Card>
      </Container>
    );
  }

  if (pageState === "onboarding-check-failed") {
    return (
      <ServiceUnavailable
        title="We couldn't confirm your profile status"
        description={
          detailMessage ??
          "We're unable to confirm whether you still need to set up a screen name. Please retry in a moment."
        }
        onRetry={() => void checkSessionStatus()}
        isRetrying={authStatus === "loading"}
      />
    );
  }

  // invalid | expired | revoked | unavailable | forbidden — NEVER shows campaign details.
  if (pageState !== "ready" && pageState !== "joining") {
    const copy: Record<string, { title: string; body: string }> = {
      invalid: { title: "Invite Not Valid", body: "This invite link is not valid." },
      expired: { title: "Invite Expired", body: "This invite link has expired. Ask the GM for a new one." },
      revoked: { title: "Invite Revoked", body: "This invite link was revoked by the GM." },
      forbidden: {
        title: "Not Permitted",
        body: detailMessage ?? "You don't have permission to join this campaign.",
      },
      unavailable: {
        title: "Service Unavailable",
        body: detailMessage ?? "We couldn't reach the service right now. Please try again shortly.",
      },
    };
    const { title, body } = copy[pageState] ?? copy.invalid;
    return (
      <Container maxW="md" py={20}>
        <Card variant="elevated" borderWidth={2} borderColor="red.200">
          <CardHeader>
            <HStack>
              <AlertCircle size={24} color="red" />
              <Heading size="md">{title}</Heading>
            </HStack>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={4} align="start">
              <Text>{body}</Text>
              <Button onClick={() => navigate("/")} leftIcon={<Home size={16} />} w="full">
                Back to Home
              </Button>
            </VStack>
          </CardBody>
        </Card>
      </Container>
    );
  }

  // ready | joining — the main preview + join UI.
  if (!campaignInfo) return null; // unreachable (previewStatus === 'loaded' guarantees this), satisfies TS
  return (
    <Container maxW="md" py={20}>
      <Fade in={true}>
        <Card variant="elevated" borderWidth={2} borderColor="blue.200">
          <CardHeader bg="blue.50" _dark={{ bg: "blue.900" }}>
            <Heading size="lg">You're Invited!</Heading>
          </CardHeader>
          <Divider />
          <CardBody>
            <VStack spacing={6} align="start">
              <VStack align="start" spacing={2} w="full">
                <Text fontSize="sm" color="gray.600">
                  Campaign Name
                </Text>
                <Heading size="md">{campaignInfo.campaign_name}</Heading>
              </VStack>

              {campaignInfo.expires_at && (
                <Box w="full" bg="yellow.50" p={3} borderRadius="md" _dark={{ bg: "yellow.900" }}>
                  <HStack spacing={2}>
                    <AlertCircle size={16} />
                    <Text fontSize="sm">
                      Invite expires: {new Date(campaignInfo.expires_at).toLocaleDateString()}
                    </Text>
                  </HStack>
                </Box>
              )}

              {campaignInfo.participant_screen_names.length > 0 && (
                <VStack align="start" spacing={1} w="full">
                  <Text fontSize="sm" color="gray.600">
                    Already playing:
                  </Text>
                  <HStack wrap="wrap" spacing={2}>
                    {campaignInfo.participant_screen_names.map((name) => (
                      <Badge key={name}>{name}</Badge>
                    ))}
                  </HStack>
                </VStack>
              )}

              {campaignInfo.prior_session_count > 0 && (
                <Text fontSize="sm" color="gray.600">
                  {campaignInfo.prior_session_count} session
                  {campaignInfo.prior_session_count === 1 ? "" : "s"} played so far.
                </Text>
              )}

              <VStack w="full" spacing={2}>
                <Button
                  onClick={handleJoinCampaign}
                  isLoading={pageState === "joining"}
                  disabled={pageState === "joining"}
                  colorScheme="green"
                  size="lg"
                  w="full"
                  leftIcon={<Users size={18} />}
                >
                  {pageState === "joining" ? "Joining..." : "Join Campaign"}
                </Button>

                <Button onClick={() => navigate("/")} variant="ghost" w="full" disabled={pageState === "joining"}>
                  Cancel
                </Button>
              </VStack>

              <Text fontSize="xs" color="gray.500">
                By joining, you'll become a member of this campaign and can view shared
                characters, chat, and participate in sessions.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </Fade>
    </Container>
  );
};

export default InviteLanding;
